import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Building2, Users, Phone, Target, X, TrendingUp, Clock, Check, AlertCircle, FileText } from 'lucide-react';
import { HistoricalTrendsTab } from '@/components/pulse-operations/HistoricalTrendsTab';

export default function GymDrilldownModal({ gymId, date, open, onClose }) {
  const [data, setData] = useState(null);
  const [pulseEntries, setPulseEntries] = useState({ p1: null, p2: null, p3: null, p4: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && gymId) {
      fetchDrilldown();
    }
  }, [open, gymId, date]);

  const fetchDrilldown = async () => {
    setLoading(true);
    try {
      const params = date ? { date } : {};
      const [drilldownResponse, pulseEntriesResponse] = await Promise.all([
        axiosInstance.get(`/gym-drilldown/${gymId}`, { params }),
        axiosInstance.get('/pulse-entries', { params: { gym_id: gymId, date: date || new Date().toISOString().split('T')[0] } })
      ]);

      setData(drilldownResponse.data);

      // Group pulse entries by pulse ID (P1 -> p1, P2 -> p2, etc.)
      const grouped = { p1: null, p2: null, p3: null, p4: null };
      pulseEntriesResponse.data.forEach(entry => {
        const pulseKey = entry.pulse_id?.toLowerCase(); // P2 becomes p2
        if (pulseKey && pulseKey in grouped) {
          grouped[pulseKey] = entry;
        }
      });
      setPulseEntries(grouped);
    } catch (error) {
      toast.error('Failed to load gym details');
    } finally {
      setLoading(false);
    }
  };

  // Helper component to display pulse entry details
  const PulseDetailsCard = ({ entry }) => {
    if (!entry) return null;

    const connectRate = entry.dialed > 0 ? ((entry.connected / entry.dialed) * 100).toFixed(1) : 0;

    return (
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Dialed</p>
              <p className="text-2xl font-bold">{entry.dialed}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Connected</p>
              <p className="text-2xl font-bold">{entry.connected}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Connect Rate</p>
              <p className="text-2xl font-bold" style={{
                color: connectRate >= 70 ? '#10b981' : connectRate >= 60 ? '#f59e0b' : '#ef4444'
              }}>
                {connectRate}%
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Visit Scheduled</p>
              <p className="text-2xl font-bold text-blue-600">{entry.visit_scheduled || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Visit Done</p>
              <p className="text-2xl font-bold text-green-600">{entry.visit_done || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Closures</p>
              <p className="text-2xl font-bold text-emerald-600">{entry.closures}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Appointments</p>
              <p className="text-2xl font-bold text-purple-600">{entry.appointments || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">FD Availability</p>
              <p className="text-2xl font-bold">{entry.fd_availability !== null ? entry.fd_availability : '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Call Source</p>
              <p className="text-sm font-medium">
                {entry.dialed_calls_source === 'company_ivr' ? 'Company IVR' :
                 entry.dialed_calls_source === 'fd_phone' ? 'FD Phone' : 'N/A'}
              </p>
            </div>
            {entry.revenue && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Revenue</p>
                <p className="text-xl font-bold text-green-600">₹{entry.revenue.toLocaleString()}</p>
              </div>
            )}
            <div className="col-span-2 md:col-span-3">
              <p className="text-sm text-muted-foreground mb-1">Verification Status</p>
              <div className="flex items-center gap-3">
                <Badge variant={entry.tl_verified ? 'default' : 'secondary'}
                       className={entry.tl_verified ? 'bg-green-100 text-green-700 border-green-300' : ''}>
                  TL: {entry.tl_verified ? 'Verified' : 'Pending'}
                </Badge>
                <Badge variant={entry.mgr_verified ? 'default' : 'secondary'}
                       className={entry.mgr_verified ? 'bg-green-100 text-green-700 border-green-300' : ''}>
                  Manager: {entry.mgr_verified ? 'Verified' : 'Pending'}
                </Badge>
              </div>
              {entry.verification_date && (
                <p className="text-xs text-muted-foreground mt-2">
                  Verified: {new Date(entry.verification_date).toLocaleString()} by {entry.verifier_name || 'Unknown'}
                </p>
              )}
            </div>
            {entry.notes && (
              <div className="col-span-2 md:col-span-3">
                <p className="text-sm text-muted-foreground mb-1">Notes</p>
                <p className="text-sm bg-muted/30 p-3 rounded-lg">{entry.notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Helper component for empty pulse state
  const EmptyPulseCard = ({ pulseName }) => {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-semibold text-muted-foreground">No data for {pulseName}</p>
          <p className="text-sm text-muted-foreground mt-2">
            This pulse has not been filled yet or is not available for the selected date.
          </p>
        </CardContent>
      </Card>
    );
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-primary" />
              <div>
                <DialogTitle className="text-xl">{data?.gym?.name || 'Loading...'}</DialogTitle>
                <p className="text-sm text-muted-foreground">{data?.gym?.location} • {data?.date}</p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner" />
          </div>
        ) : data ? (
          <div className="space-y-6 mt-4">
            {/* Gym Info */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    Team Leader
                  </div>
                  <p className="font-semibold">{data.gym.tl_name}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    Manager
                  </div>
                  <p className="font-semibold">{data.gym.manager_name}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    Front Desk
                  </div>
                  <p className="font-semibold">{data.gym.fd_count} staff</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabbed Pulse Details and Historical Trends */}
            <Tabs defaultValue="p1" className="mt-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="p1">Pulse 1</TabsTrigger>
                <TabsTrigger value="p2">Pulse 2</TabsTrigger>
                <TabsTrigger value="p3">Pulse 3</TabsTrigger>
                <TabsTrigger value="p4">Pulse 4</TabsTrigger>
                <TabsTrigger value="trends">Historical Trends</TabsTrigger>
              </TabsList>

              <TabsContent value="p1" className="mt-4">
                {pulseEntries.p1 ? (
                  <PulseDetailsCard entry={pulseEntries.p1} />
                ) : (
                  <EmptyPulseCard pulseName="Pulse 1" />
                )}
              </TabsContent>

              <TabsContent value="p2" className="mt-4">
                {pulseEntries.p2 ? (
                  <PulseDetailsCard entry={pulseEntries.p2} />
                ) : (
                  <EmptyPulseCard pulseName="Pulse 2" />
                )}
              </TabsContent>

              <TabsContent value="p3" className="mt-4">
                {pulseEntries.p3 ? (
                  <PulseDetailsCard entry={pulseEntries.p3} />
                ) : (
                  <EmptyPulseCard pulseName="Pulse 3" />
                )}
              </TabsContent>

              <TabsContent value="p4" className="mt-4">
                {pulseEntries.p4 ? (
                  <PulseDetailsCard entry={pulseEntries.p4} />
                ) : (
                  <EmptyPulseCard pulseName="Pulse 4" />
                )}
              </TabsContent>

              <TabsContent value="trends" className="mt-4">
                <HistoricalTrendsTab gymId={gymId} currentDate={date} />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
